import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Text from '@cartwhl/ui/base/Text';

import { type Skeleton } from '../../../lib/skeleton/core';
import type { MappingResult } from '../../../lib/recognize/config';
import { getChainCssColor } from '../../../presentations/chain';
import {
  PickEndBoneIcon,
  PickStartBoneIcon,
} from '../../../presentations/icons';
import {
  PANEL_WRITING_CLASSNAME,
  PANEL_WRITING_VARIANT,
} from '../../../presentations/panelWritingStyle';
import {
  BONE_ASSIGNMENT_CHAIN_PATHS,
  BONE_ASSIGNMENT_DIAGRAMS,
  BONE_ASSIGNMENT_ROLE_NAMES,
  BODY_ASSIGNMENT_DIAGRAM,
  HAND_ASSIGNMENT_DIAGRAMS,
} from '../constants';
import { deriveAdditionalChainSections } from '../additionalChainUtils';
import type {
  BoneAssignmentChainState,
  BoneAssignmentDiagramChainNodeMarker,
  BoneAssignmentDiagramChainOverlaySegment,
  BoneAssignmentMode,
  BoneAssignmentsViewerPickMode,
  BoneAssignmentsViewerFocus,
  BoneAssignmentRoleState,
  HoveredBoneAssignmentChain,
} from '../types';
import {
  getChainForBone,
  formatAssignmentLabel,
  getBoneDisplayName,
  getRoleForBone,
} from '../utils';
import AdditionalChainDiagramSection from './AdditionalChainDiagramSection';
import AdditionalRoleDiagramSection from './AdditionalRoleDiagramSection';
import BoneAssignmentDiagram from './BoneAssignmentDiagram';
import BoneAssignmentCategorySection from './BoneAssignmentCategorySection';
import BoneAssignmentModeToggle from './BoneAssignmentModeToggle';
import { useViewerHoveredBoneName } from '../viewerBoneHoverEvent';

interface BoneAssignmentsProps {
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
  mode: BoneAssignmentMode;
  selectedBoneIndex: number | null;
  selectionVersion: number;
  clearSelectionToken?: number;
  onModeChange?: (mode: BoneAssignmentMode) => void;
  onAssignRole?: (boneName: string, nextRole: string | null) => void;
  onSelectBone?: (boneIndex: number | null) => void;
  onSetChainFromStartEnd?: (
    chainName: string,
    startBoneRef: string,
    endBoneRef: string,
  ) => void;
  onClearChain?: (chainName: string) => void;
  onTitleChange?: (title: string) => void;
  onViewerRoleTagChange?: (roleTag: {
    boneIndex: number;
    role: string;
    confidence: number | null;
  } | null) => void;
  onViewerHoveredChainChange?: (
    hoveredChain: HoveredBoneAssignmentChain | null,
  ) => void;
  onViewerPickModeChange?: (mode: BoneAssignmentsViewerPickMode | null) => void;
  onViewerFocusChange?: (focus: BoneAssignmentsViewerFocus | null) => void;
}

interface PendingRoleAssignment {
  roleName: string;
  lastHandledBoneIndex: number | null;
  lastHandledSelectionVersion: number;
  ignoredBoneRef: string | null;
}

interface PendingChainAssignment {
  chainName: string;
  phase: 'pick-start' | 'pick-end';
  startBoneRef: string | null;
  lastHandledBoneIndex: number | null;
  lastHandledSelectionVersion: number;
}

const ROLE_CURSOR_HINT_WIDTH_PX = 248;
const CHAIN_CURSOR_HINT_WIDTH_PX = 168;
const CURSOR_HINT_MAX_WIDTH_PX = 420;
const CURSOR_HINT_CHAR_WIDTH_PX = 7.4;
const HOVERED_BONE_NAME_MAX_WIDTH_PX = 220;
const CURSOR_HINT_HEIGHT_PX = 36;
const CURSOR_HINT_OFFSET_PX = 8;
const CURSOR_HINT_EDGE_PADDING_PX = 8;
const HOVER_RESTORE_DELAY_MS = 420;
const EMPTY_CHAIN_ASSIGNMENT: BoneAssignmentChainState = {
  boneRefs: [],
  startBoneRef: null,
  startBoneName: null,
  endBoneRef: null,
  endBoneName: null,
};
const HAND_ROLE_NAME_SET = new Set(
  HAND_ASSIGNMENT_DIAGRAMS.flatMap((diagram) =>
    diagram.nodes.map((node) => node.roleName),
  ),
);

function useDelayedHoverName(delayMs: number) {
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);

  const clearPendingReset = useCallback(() => {
    if (resetTimeoutRef.current === null) return;
    window.clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = null;
  }, []);

  const setHoveredNameWithDelay = useCallback(
    (nextHoveredName: string | null) => {
      clearPendingReset();
      if (nextHoveredName !== null) {
        setHoveredName(nextHoveredName);
        return;
      }

      // Keep the last hovered target visible during tiny pointer gaps.
      resetTimeoutRef.current = window.setTimeout(() => {
        resetTimeoutRef.current = null;
        setHoveredName(null);
      }, delayMs);
    },
    [clearPendingReset, delayMs],
  );

  const clearHoveredName = useCallback(() => {
    clearPendingReset();
    setHoveredName(null);
  }, [clearPendingReset]);

  useEffect(() => clearPendingReset, [clearPendingReset]);

  return [hoveredName, setHoveredNameWithDelay, clearHoveredName] as const;
}

function getCursorHintWidthPx(
  baseWidthPx: number,
  hoveredBoneName: string | null,
) {
  if (!hoveredBoneName) return baseWidthPx;

  const hoveredNameWidthPx = Math.min(
    HOVERED_BONE_NAME_MAX_WIDTH_PX,
    Math.ceil(hoveredBoneName.length * CURSOR_HINT_CHAR_WIDTH_PX) + 12,
  );
  return Math.min(CURSOR_HINT_MAX_WIDTH_PX, baseWidthPx + hoveredNameWidthPx);
}

export default function BoneAssignments({
  skeleton,
  mapping,
  mode,
  selectedBoneIndex,
  selectionVersion,
  clearSelectionToken = 0,
  onModeChange,
  onAssignRole,
  onSelectBone,
  onSetChainFromStartEnd,
  onClearChain,
  onTitleChange,
  onViewerRoleTagChange,
  onViewerHoveredChainChange,
  onViewerPickModeChange,
  onViewerFocusChange,
}: BoneAssignmentsProps) {
  const assignmentMode = mode;
  const hoveredViewerBoneName = useViewerHoveredBoneName();
  const [activeRoleName, setActiveRoleName] = useState<string | null>(null);
  const [activeChainName, setActiveChainName] = useState<string | null>(null);
  const [hoveredRoleName, handleHoveredRoleChange, clearHoveredRoleName] =
    useDelayedHoverName(HOVER_RESTORE_DELAY_MS);
  const [hoveredChainName, handleHoveredChainChange, clearHoveredChainName] =
    useDelayedHoverName(HOVER_RESTORE_DELAY_MS);
  const [pendingRoleAssignment, setPendingRoleAssignment] =
    useState<PendingRoleAssignment | null>(null);
  const [pendingChainAssignment, setPendingChainAssignment] =
    useState<PendingChainAssignment | null>(null);
  const [cursorHintPosition, setCursorHintPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const lastExternalClearSelectionTokenRef = useRef(clearSelectionToken);
  const [dismissedRoleSelectionVersion, setDismissedRoleSelectionVersion] =
    useState<number | null>(null);
  const [dismissedChainSelectionVersion, setDismissedChainSelectionVersion] =
    useState<number | null>(null);
  const roleAssignmentEnabled = Boolean(mapping && onAssignRole);
  const chainAssignmentEnabled = Boolean(mapping && onSetChainFromStartEnd);
  const presentChainNames = useMemo(
    () => new Set(Object.keys(mapping?.chains ?? {})),
    [mapping],
  );
  const selectedBoneRef =
    selectedBoneIndex !== null
      ? (skeleton?.bones[selectedBoneIndex]?.internalRef ?? null)
      : null;
  const selectedBoneRoleName = useMemo(
    () => getRoleForBone(mapping, selectedBoneRef),
    [mapping, selectedBoneRef],
  );
  const selectedBoneChainName = useMemo(
    () => getChainForBone(mapping, selectedBoneRef),
    [mapping, selectedBoneRef],
  );
  const chainAssignments = useMemo(() => {
    const assignments = new Map<string, BoneAssignmentChainState>();
    for (const chainName of presentChainNames) {
      const boneRefs = mapping?.chains[chainName] ?? [];
      const startBoneRef =
        mapping?.chainResults[chainName]?.startBone ?? boneRefs[0] ?? null;
      const endBoneRef =
        mapping?.chainResults[chainName]?.endBone ?? boneRefs[boneRefs.length - 1] ?? null;

      assignments.set(chainName, {
        boneRefs,
        startBoneRef,
        startBoneName: getBoneDisplayName(skeleton, startBoneRef),
        endBoneRef,
        endBoneName: getBoneDisplayName(skeleton, endBoneRef),
      });
    }
    return assignments;
  }, [mapping, presentChainNames, skeleton]);
  const draftChainNames = useMemo(
    () =>
      new Set(
        [activeChainName, pendingChainAssignment?.chainName]
          .filter((chainName): chainName is string => Boolean(chainName))
          .filter((chainName) => !presentChainNames.has(chainName)),
      ),
    [activeChainName, pendingChainAssignment?.chainName, presentChainNames],
  );
  const additionalChainSections = useMemo(
    () =>
      deriveAdditionalChainSections({
        presentChainNames,
        draftChainNames,
        chainAssignments,
      }),
    [chainAssignments, draftChainNames, presentChainNames],
  );
  const additionalRoleSections = additionalChainSections;
  const additionalRoleNames = useMemo(
    () =>
      additionalRoleSections.flatMap((section) => [
        ...section.families.flatMap((familyGroup) =>
          familyGroup.instances.flatMap((instance) => instance.roleNames),
        ),
        ...section.subsections.flatMap((subsection) =>
          subsection.families.flatMap((familyGroup) =>
            familyGroup.instances.flatMap((instance) => instance.roleNames),
          ),
        ),
      ]),
    [additionalRoleSections],
  );
  const roleAssignments = useMemo(() => {
    const assignments = new Map<string, BoneAssignmentRoleState>();
    const roleNames = new Set<string>([
      ...BONE_ASSIGNMENT_ROLE_NAMES,
      ...additionalRoleNames,
      ...Object.keys(mapping?.roleToBone ?? {}),
      ...Object.keys(mapping?.roleToConfidence ?? {}),
    ]);

    for (const roleName of roleNames) {
      const boneRef = mapping?.roleToBone[roleName] ?? null;
      assignments.set(roleName, {
        boneRef,
        boneName: getBoneDisplayName(skeleton, boneRef),
        confidence: mapping?.roleToConfidence[roleName] ?? null,
      });
    }

    return assignments;
  }, [additionalRoleNames, mapping, skeleton]);
  const assignedRoleNames = useMemo(
    () =>
      new Set(
        [...roleAssignments.entries()]
          .filter(([, assignment]) => assignment.boneRef !== null)
          .map(([roleName]) => roleName),
      ),
    [roleAssignments],
  );
  const autoFocusedRoleName =
    dismissedRoleSelectionVersion === selectionVersion ? null : selectedBoneRoleName;
  const focusedRoleName = activeRoleName ?? autoFocusedRoleName;
  const autoFocusedChainName =
    dismissedChainSelectionVersion === selectionVersion ? null : selectedBoneChainName;
  const focusedChainName = activeChainName ?? autoFocusedChainName;
  const activeRoleAssignment = activeRoleName
    ? (roleAssignments.get(activeRoleName) ?? null)
    : null;
  const activeChainAssignment = activeChainName
    ? (chainAssignments.get(activeChainName) ?? EMPTY_CHAIN_ASSIGNMENT)
    : null;
  const chainOverlayByDiagramKey = useMemo(() => {
    const overlayByDiagramKey = new Map<
      string,
      {
        segments: BoneAssignmentDiagramChainOverlaySegment[];
        nodeMarkers: BoneAssignmentDiagramChainNodeMarker[];
      }
    >();

    for (const diagram of BONE_ASSIGNMENT_DIAGRAMS) {
      overlayByDiagramKey.set(diagram.key, { segments: [], nodeMarkers: [] });
    }

    for (const chainPath of BONE_ASSIGNMENT_CHAIN_PATHS) {
      const chainState =
        chainAssignments.get(chainPath.chainName) ?? EMPTY_CHAIN_ASSIGNMENT;
      const isAssigned = chainState.boneRefs.length > 0;
      const isFocused = focusedChainName === chainPath.chainName;
      const isPending = pendingChainAssignment?.chainName === chainPath.chainName;

      const color = getChainCssColor(chainPath.chainName);
      for (const segment of chainPath.segments) {
        overlayByDiagramKey.get(segment.diagramKey)?.segments.push({
          chainName: chainPath.chainName,
          from: segment.from,
          to: segment.to,
          startRole: chainPath.startRole,
          endRole: chainPath.endRole,
          startBoneName: chainState.startBoneName,
          endBoneName: chainState.endBoneName,
          color,
          isAssigned,
          isFocused,
          isPending,
        });
      }

      if (chainPath.segments.length === 0 && chainPath.nodeDiagramKey) {
        overlayByDiagramKey.get(chainPath.nodeDiagramKey)?.nodeMarkers.push({
          chainName: chainPath.chainName,
          roleName: chainPath.startRole,
          startRole: chainPath.startRole,
          endRole: chainPath.endRole,
          startBoneName: chainState.startBoneName,
          endBoneName: chainState.endBoneName,
          color,
          isAssigned,
          isFocused,
          isPending,
        });
      }
    }

    for (const overlay of overlayByDiagramKey.values()) {
      overlay.segments.sort((left, right) =>
        getChainOverlayPriority(left, hoveredChainName) -
        getChainOverlayPriority(right, hoveredChainName),
      );
      overlay.nodeMarkers.sort((left, right) =>
        getChainOverlayPriority(left, hoveredChainName) -
        getChainOverlayPriority(right, hoveredChainName),
      );
    }

    return overlayByDiagramKey;
  }, [
    chainAssignments,
    focusedChainName,
    hoveredChainName,
    pendingChainAssignment,
  ]);
  const pendingHintRoleName =
    assignmentMode === 'roles' ? (pendingRoleAssignment?.roleName ?? null) : null;
  const shouldShowPickRoleHint = Boolean(
    pendingHintRoleName &&
      (roleAssignments.get(pendingHintRoleName)?.boneRef ?? null) === null,
  );
  const handsSectionDefaultOpen = useMemo(() => {
    if (assignmentMode === 'roles') {
      if (
        (focusedRoleName !== null && HAND_ROLE_NAME_SET.has(focusedRoleName)) ||
        (pendingRoleAssignment?.roleName !== undefined &&
          pendingRoleAssignment.roleName !== null &&
          HAND_ROLE_NAME_SET.has(pendingRoleAssignment.roleName))
      ) {
        return true;
      }

      return HAND_ASSIGNMENT_DIAGRAMS.some((diagram) =>
        diagram.nodes.some(
          (node) => (roleAssignments.get(node.roleName)?.boneRef ?? null) !== null,
        ),
      );
    }

    return HAND_ASSIGNMENT_DIAGRAMS.some((diagram) => {
      const overlay = chainOverlayByDiagramKey.get(diagram.key);
      if (!overlay) return false;

      return (
        overlay.segments.some(
          (segment) => segment.isAssigned || segment.isFocused || segment.isPending,
        ) ||
        overlay.nodeMarkers.some(
          (marker) => marker.isAssigned || marker.isFocused || marker.isPending,
        )
      );
    });
  }, [
    assignmentMode,
    chainOverlayByDiagramKey,
    focusedRoleName,
    pendingRoleAssignment,
    roleAssignments,
  ]);
  const shouldShowChainHint =
    assignmentMode === 'chains' && Boolean(pendingChainAssignment);
  const pickRoleHintLabel = `Pick "${formatAssignmentLabel(pendingHintRoleName ?? '')}" bone`;
  const chainHintLabel =
    pendingChainAssignment?.phase === 'pick-start'
      ? 'Pick Start Bone'
      : 'Pick End Bone';
  const cursorHintWidthPx = shouldShowPickRoleHint
    ? getCursorHintWidthPx(ROLE_CURSOR_HINT_WIDTH_PX, hoveredViewerBoneName)
    : shouldShowChainHint
      ? getCursorHintWidthPx(CHAIN_CURSOR_HINT_WIDTH_PX, hoveredViewerBoneName)
      : 0;

  useEffect(() => {
    clearHoveredRoleName();
    clearHoveredChainName();
    if (assignmentMode === 'roles') {
      setPendingChainAssignment(null);
      return;
    }
    setPendingRoleAssignment(null);
  }, [assignmentMode, clearHoveredChainName, clearHoveredRoleName]);

  useEffect(() => {
    if (!roleAssignmentEnabled) {
      setPendingRoleAssignment(null);
    }
  }, [roleAssignmentEnabled]);

  useEffect(() => {
    if (!chainAssignmentEnabled) {
      setPendingChainAssignment(null);
    }
  }, [chainAssignmentEnabled]);

  useEffect(() => {
    if (!shouldShowPickRoleHint && !shouldShowChainHint) {
      setCursorHintPosition(null);
    }
  }, [shouldShowChainHint, shouldShowPickRoleHint]);

  useEffect(() => {
    if (!onTitleChange) return;

    if (assignmentMode === 'chains') {
      if (!activeChainName) {
        onTitleChange('Bone Assignments');
        return;
      }
      onTitleChange(
        `${formatAssignmentLabel(activeChainName)} -> ${formatChainAssignmentTitle(
          activeChainAssignment,
        )}`,
      );
      return;
    }

    if (!activeRoleName) {
      onTitleChange('Bone Assignments');
      return;
    }
    onTitleChange(
      `${formatAssignmentLabel(activeRoleName)} -> ${
        activeRoleAssignment?.boneName ?? 'Unassigned'
      }`,
    );
  }, [
    activeChainAssignment,
    activeChainName,
    activeRoleAssignment?.boneName,
    activeRoleName,
    assignmentMode,
    onTitleChange,
  ]);

  useEffect(() => {
    if (!onViewerRoleTagChange) return;
    if (
      assignmentMode !== 'roles' ||
      !activeRoleName ||
      !activeRoleAssignment?.boneRef ||
      !skeleton
    ) {
      onViewerRoleTagChange(null);
      return;
    }
    const boneIndex = skeleton.resolveBoneRef(activeRoleAssignment.boneRef, {
      namespace: 'internal',
    });
    if (boneIndex === null) {
      onViewerRoleTagChange(null);
      return;
    }
    onViewerRoleTagChange({
      boneIndex,
      role: activeRoleName,
      confidence: activeRoleAssignment.confidence,
    });
  }, [
    activeRoleAssignment?.boneRef,
    activeRoleAssignment?.confidence,
    activeRoleName,
    assignmentMode,
    onViewerRoleTagChange,
    skeleton,
  ]);

  useEffect(() => {
    if (!onViewerHoveredChainChange) return;
    if (assignmentMode !== 'chains' || !hoveredChainName || !skeleton) {
      onViewerHoveredChainChange(null);
      return;
    }

    const hoveredChainAssignment =
      chainAssignments.get(hoveredChainName) ?? EMPTY_CHAIN_ASSIGNMENT;
    const startBoneIndex = hoveredChainAssignment.startBoneRef
      ? skeleton.resolveBoneRef(hoveredChainAssignment.startBoneRef, {
          namespace: 'internal',
        })
      : null;
    const endBoneIndex = hoveredChainAssignment.endBoneRef
      ? skeleton.resolveBoneRef(hoveredChainAssignment.endBoneRef, {
          namespace: 'internal',
        })
      : null;

    if (startBoneIndex === null && endBoneIndex === null) {
      onViewerHoveredChainChange(null);
      return;
    }

    onViewerHoveredChainChange({
      chainName: hoveredChainName,
      startBoneIndex,
      startBoneName: hoveredChainAssignment.startBoneName,
      endBoneIndex,
      endBoneName: hoveredChainAssignment.endBoneName,
    });
  }, [
    assignmentMode,
    chainAssignments,
    hoveredChainName,
    onViewerHoveredChainChange,
    skeleton,
  ]);

  useEffect(() => {
    if (!onViewerPickModeChange) return;
    if (assignmentMode === 'roles' && pendingRoleAssignment) {
      onViewerPickModeChange({
        kind: 'role',
        roleName: pendingRoleAssignment.roleName,
      });
      return;
    }
    if (assignmentMode === 'chains' && pendingChainAssignment) {
      onViewerPickModeChange({
        kind: 'chain',
        chainName: pendingChainAssignment.chainName,
        phase: pendingChainAssignment.phase,
      });
      return;
    }
    onViewerPickModeChange(null);
  }, [
    assignmentMode,
    onViewerPickModeChange,
    pendingChainAssignment,
    pendingRoleAssignment,
  ]);

  useEffect(() => {
    if (!onViewerFocusChange) return;
    if (assignmentMode === 'roles') {
      const focusedViewerRoleName = hoveredRoleName ?? focusedRoleName;
      onViewerFocusChange(
        focusedViewerRoleName
          ? {
              mode: 'roles',
              roleName: focusedViewerRoleName,
            }
          : null,
      );
      return;
    }

    const focusedViewerChainName = hoveredChainName ?? focusedChainName;
    onViewerFocusChange(
      focusedViewerChainName
        ? {
            mode: 'chains',
            chainName: focusedViewerChainName,
          }
        : null,
    );
  }, [
    assignmentMode,
    focusedChainName,
    focusedRoleName,
    hoveredChainName,
    hoveredRoleName,
    onViewerFocusChange,
  ]);

  useEffect(
    () => () => {
      onViewerRoleTagChange?.(null);
      onViewerHoveredChainChange?.(null);
      onViewerPickModeChange?.(null);
      onViewerFocusChange?.(null);
    },
    [
      onViewerFocusChange,
      onViewerHoveredChainChange,
      onViewerPickModeChange,
      onViewerRoleTagChange,
    ],
  );

  useEffect(() => {
    if (
      assignmentMode !== 'roles' ||
      !pendingRoleAssignment ||
      !roleAssignmentEnabled ||
      !onAssignRole ||
      !skeleton
    ) {
      return;
    }
    if (selectedBoneIndex === null) return;
    const selectedBoneRefForAssignment = skeleton.bone(selectedBoneIndex).internalRef;
    if (
      pendingRoleAssignment.ignoredBoneRef &&
      selectedBoneRefForAssignment === pendingRoleAssignment.ignoredBoneRef
    ) {
      setPendingRoleAssignment((current) => {
        if (!current || current.roleName !== pendingRoleAssignment.roleName) {
          return current;
        }
        return {
          ...current,
          ignoredBoneRef: null,
          lastHandledBoneIndex: selectedBoneIndex,
          lastHandledSelectionVersion: selectionVersion,
        };
      });
      return;
    }
    if (
      selectedBoneIndex === pendingRoleAssignment.lastHandledBoneIndex &&
      selectionVersion === pendingRoleAssignment.lastHandledSelectionVersion
    ) {
      return;
    }

    onAssignRole(selectedBoneRefForAssignment, pendingRoleAssignment.roleName);
    setPendingRoleAssignment(null);
  }, [
    assignmentMode,
    onAssignRole,
    pendingRoleAssignment,
    roleAssignmentEnabled,
    selectedBoneIndex,
    selectionVersion,
    skeleton,
  ]);

  useEffect(() => {
    if (
      assignmentMode !== 'chains' ||
      !pendingChainAssignment ||
      !chainAssignmentEnabled ||
      !onSetChainFromStartEnd ||
      !skeleton
    ) {
      return;
    }
    if (selectedBoneIndex === null) return;
    if (
      selectedBoneIndex === pendingChainAssignment.lastHandledBoneIndex &&
      selectionVersion === pendingChainAssignment.lastHandledSelectionVersion
    ) {
      return;
    }

    const selectedBoneRefForChain = skeleton.refForBoneIndex(selectedBoneIndex);
    if (pendingChainAssignment.phase === 'pick-start') {
      setPendingChainAssignment((current) => {
        if (!current || current.chainName !== pendingChainAssignment.chainName) {
          return current;
        }
        return {
          ...current,
          phase: 'pick-end',
          startBoneRef: selectedBoneRefForChain,
          lastHandledBoneIndex: selectedBoneIndex,
          lastHandledSelectionVersion: selectionVersion,
        };
      });
      return;
    }

    if (!pendingChainAssignment.startBoneRef) return;
    onSetChainFromStartEnd(
      pendingChainAssignment.chainName,
      pendingChainAssignment.startBoneRef,
      selectedBoneRefForChain,
    );
    setPendingChainAssignment(null);
  }, [
    assignmentMode,
    chainAssignmentEnabled,
    onSetChainFromStartEnd,
    pendingChainAssignment,
    selectedBoneIndex,
    selectionVersion,
    skeleton,
  ]);

  useEffect(() => {
    if (!shouldShowPickRoleHint && !shouldShowChainHint) return;

    const handleMouseMove = (event: MouseEvent) => {
      const viewerArea = document.querySelector(
        '[data-cartographer-viewer-area="true"]',
      );
      if (!(viewerArea instanceof HTMLElement)) return;

      const rect = viewerArea.getBoundingClientRect();
      const isWithinViewerArea =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isWithinViewerArea) {
        setCursorHintPosition(null);
        return;
      }

      const minX = rect.left + CURSOR_HINT_EDGE_PADDING_PX;
      const minY = rect.top + CURSOR_HINT_EDGE_PADDING_PX;
      const maxX = Math.max(
        minX,
        rect.right - cursorHintWidthPx - CURSOR_HINT_EDGE_PADDING_PX,
      );
      const maxY = Math.max(
        minY,
        rect.bottom - CURSOR_HINT_HEIGHT_PX - CURSOR_HINT_EDGE_PADDING_PX,
      );
      const nextX = event.clientX + CURSOR_HINT_OFFSET_PX;
      const nextY = event.clientY + CURSOR_HINT_OFFSET_PX;

      setCursorHintPosition({
        x: Math.min(maxX, Math.max(minX, nextX)),
        y: Math.min(maxY, Math.max(minY, nextY)),
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cursorHintWidthPx, shouldShowChainHint, shouldShowPickRoleHint]);

  const handleRoleClick = (roleName: string) => {
    setActiveRoleName(roleName);
    setDismissedRoleSelectionVersion(null);
    if (!roleAssignmentEnabled) return;
    const assignedBoneRef = roleAssignments.get(roleName)?.boneRef ?? null;
    if (assignedBoneRef && skeleton && onSelectBone) {
      const assignedBoneIndex = skeleton.resolveBoneRef(assignedBoneRef, {
        namespace: 'internal',
      });
      if (assignedBoneIndex !== null) {
        onSelectBone(assignedBoneIndex);
      }
    }
    setPendingRoleAssignment((current) =>
      current?.roleName === roleName &&
      current.ignoredBoneRef === assignedBoneRef &&
      current.lastHandledBoneIndex === selectedBoneIndex &&
      current.lastHandledSelectionVersion === selectionVersion
        ? current
        : {
            roleName,
            lastHandledBoneIndex: selectedBoneIndex,
            lastHandledSelectionVersion: selectionVersion,
            ignoredBoneRef: assignedBoneRef,
          },
    );
  };

  const handleRoleClear = (roleName: string) => {
    const assignedBoneRef = roleAssignments.get(roleName)?.boneRef ?? null;
    if (!assignedBoneRef || !onAssignRole) return;
    onAssignRole(assignedBoneRef, null);
    if (selectedBoneRef === assignedBoneRef) {
      onSelectBone?.(null);
    }
    setPendingRoleAssignment((current) =>
      current?.roleName === roleName ? null : current,
    );
  };

  const handleChainClick = (chainName: string) => {
    setActiveChainName(chainName);
    setDismissedChainSelectionVersion(null);
    if (!chainAssignmentEnabled) return;
    setPendingChainAssignment({
      chainName,
      phase: 'pick-start',
      startBoneRef: null,
      lastHandledBoneIndex: selectedBoneIndex,
      lastHandledSelectionVersion: selectionVersion,
    });
  };

  const handleAdditionalChainClear = useCallback(
    (chainNames: readonly string[]) => {
      const removableChainNameSet = new Set(chainNames);
      const additionalInstances = additionalChainSections.flatMap((section) => [
        ...section.families.flatMap((familyGroup) => familyGroup.instances),
        ...section.subsections.flatMap((subsection) =>
          subsection.families.flatMap((familyGroup) => familyGroup.instances),
        ),
      ]);

      for (const instance of additionalInstances) {
        if (
          !instance.members.some((member) =>
            removableChainNameSet.has(member.chainName),
          )
        ) {
          continue;
        }

        if (instance.family.key !== 'leftArm' && instance.family.key !== 'rightArm') {
          continue;
        }

        const sourceBaseChainName =
          instance.family.key === 'leftArm' ? 'LeftArm' : 'RightArm';

        for (const relatedInstance of additionalInstances) {
          if (
            relatedInstance.instanceIndex !== instance.instanceIndex ||
            relatedInstance.family.instanceSourceBaseChainName !== sourceBaseChainName
          ) {
            continue;
          }

          for (const member of relatedInstance.members) {
            removableChainNameSet.add(member.chainName);
          }
        }
      }

      const removableChainNames = Array.from(removableChainNameSet);
      if (removableChainNames.length === 0) return;

      clearHoveredChainName();
      setPendingChainAssignment((current) =>
        current && removableChainNames.includes(current.chainName) ? null : current,
      );
      setActiveChainName((current) =>
        current && removableChainNames.includes(current) ? null : current,
      );

      if (!onClearChain) return;
      for (const chainName of removableChainNames) {
        if (!presentChainNames.has(chainName)) continue;
        onClearChain(chainName);
      }
    },
    [additionalChainSections, clearHoveredChainName, onClearChain, presentChainNames],
  );
  const handleAdditionalDiagramAdd = useCallback((chainName: string) => {
    clearHoveredChainName();
    setPendingChainAssignment(null);
    setActiveChainName(chainName);
    setDismissedChainSelectionVersion(null);
  }, [clearHoveredChainName]);

  const handleModeChange = (mode: BoneAssignmentMode) => {
    if (mode === assignmentMode) return;
    clearHoveredChainName();
    setCursorHintPosition(null);
    onModeChange?.(mode);
  };

  const clearAssignerSelection = useCallback(() => {
    clearHoveredRoleName();
    clearHoveredChainName();
    if (assignmentMode === 'chains') {
      setActiveChainName(null);
      setPendingChainAssignment(null);
      setDismissedChainSelectionVersion(selectionVersion);
      return;
    }

    setActiveRoleName(null);
    setPendingRoleAssignment(null);
    setDismissedRoleSelectionVersion(selectionVersion);
  }, [
    assignmentMode,
    clearHoveredChainName,
    clearHoveredRoleName,
    selectionVersion,
  ]);

  useEffect(() => {
    if (clearSelectionToken === lastExternalClearSelectionTokenRef.current) {
      return;
    }
    lastExternalClearSelectionTokenRef.current = clearSelectionToken;
    clearAssignerSelection();
  }, [clearAssignerSelection, clearSelectionToken]);

  const cursorHint =
    cursorHintPosition && typeof document !== 'undefined'
      ? shouldShowPickRoleHint
        ? createPortal(
            <div
              className="pointer-events-none fixed z-[120]"
              style={{
                left: `${cursorHintPosition.x}px`,
                top: `${cursorHintPosition.y}px`,
              }}
            >
              <div
                className="bg-white text-black inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full px-3 py-1.5 shadow-sm"
                style={{ maxWidth: `${cursorHintWidthPx}px` }}
              >
                <Text
                  variant="studio/body/medium/sm"
                  className="shrink-0 text-black"
                >
                  {pickRoleHintLabel}
                </Text>
                <PickStartBoneIcon className="h-4 w-4 shrink-0" />
                {hoveredViewerBoneName && (
                  <Text
                    variant="studio/body/medium/sm"
                    className="min-w-0 truncate text-black"
                  >
                    {hoveredViewerBoneName}
                  </Text>
                )}
              </div>
            </div>,
            document.body,
          )
        : shouldShowChainHint && pendingChainAssignment
          ? createPortal(
              <div
                className="pointer-events-none fixed z-[120]"
                style={{
                  left: `${cursorHintPosition.x}px`,
                  top: `${cursorHintPosition.y}px`,
                }}
              >
                <div
                  className="text-white inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full px-3 py-1.5 shadow-sm"
                  style={{
                    backgroundColor: getChainCssColor(pendingChainAssignment.chainName),
                    maxWidth: `${cursorHintWidthPx}px`,
                  }}
                >
                  {pendingChainAssignment.phase === 'pick-end' && (
                    <PickEndBoneIcon className="h-4 w-4 shrink-0" />
                  )}
                  <Text
                    variant="studio/body/medium/sm"
                    className="shrink-0 text-white"
                  >
                    {chainHintLabel}
                  </Text>
                  {pendingChainAssignment.phase === 'pick-start' && (
                    <PickStartBoneIcon className="h-4 w-4 shrink-0" />
                  )}
                  {hoveredViewerBoneName && (
                    <Text
                      variant="studio/body/medium/sm"
                      className="min-w-0 truncate text-white"
                    >
                      {hoveredViewerBoneName}
                    </Text>
                  )}
                </div>
              </div>,
              document.body,
            )
          : null
      : null;

  if (!skeleton) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Text
          variant={PANEL_WRITING_VARIANT}
          className={`text-center ${PANEL_WRITING_CLASSNAME}`}
        >
          Select a node in the outliner
        </Text>
      </div>
    );
  }

  return (
    <div className="translucent-white-scrollbar dark:bg-gray-90/20 flex h-full flex-col overflow-y-auto rounded-xl bg-white/20">
      {cursorHint}
      <div className="flex flex-col gap-1 p-1" onClick={clearAssignerSelection}>
        <BoneAssignmentModeToggle
          mode={assignmentMode}
          onModeChange={handleModeChange}
        />

        {assignmentMode === 'roles' ? (
          <BoneAssignmentDiagram
            mode="roles"
            diagram={BODY_ASSIGNMENT_DIAGRAM}
            assignedRoleNames={assignedRoleNames}
            roleAssignments={roleAssignments}
            focusedRoleName={focusedRoleName}
            pendingRoleName={pendingRoleAssignment?.roleName ?? null}
            selectedBoneRef={selectedBoneRef}
            assignmentEnabled={roleAssignmentEnabled}
            formatRoleLabel={formatAssignmentLabel}
            onRoleClick={handleRoleClick}
            onHoveredRoleChange={handleHoveredRoleChange}
            onRoleClear={handleRoleClear}
          />
        ) : (
          <BoneAssignmentDiagram
            mode="chains"
            diagram={BODY_ASSIGNMENT_DIAGRAM}
            assignedRoleNames={assignedRoleNames}
            chainSegments={
              chainOverlayByDiagramKey.get(BODY_ASSIGNMENT_DIAGRAM.key)?.segments ??
              []
            }
            chainNodeMarkers={
              chainOverlayByDiagramKey.get(BODY_ASSIGNMENT_DIAGRAM.key)
                ?.nodeMarkers ?? []
            }
            hoveredChainName={hoveredChainName}
            onHoveredChainChange={handleHoveredChainChange}
            onChainClick={handleChainClick}
          />
        )}

        <BoneAssignmentCategorySection
          key={`${assignmentMode}-hands-${handsSectionDefaultOpen ? 'open' : 'closed'}`}
          title="Hands"
          defaultOpen={handsSectionDefaultOpen}
        >
          <div className="grid grid-cols-2 gap-1 pt-3">
            {HAND_ASSIGNMENT_DIAGRAMS.map((diagram) =>
              assignmentMode === 'roles' ? (
                <BoneAssignmentDiagram
                  key={diagram.key}
                  mode="roles"
                  diagram={diagram}
                  assignedRoleNames={assignedRoleNames}
                  roleAssignments={roleAssignments}
                  focusedRoleName={focusedRoleName}
                  pendingRoleName={pendingRoleAssignment?.roleName ?? null}
                  selectedBoneRef={selectedBoneRef}
                  assignmentEnabled={roleAssignmentEnabled}
                  formatRoleLabel={formatAssignmentLabel}
                  onRoleClick={handleRoleClick}
                  onHoveredRoleChange={handleHoveredRoleChange}
                  onRoleClear={handleRoleClear}
                />
              ) : (
                <BoneAssignmentDiagram
                  key={diagram.key}
                  mode="chains"
                  diagram={diagram}
                  assignedRoleNames={assignedRoleNames}
                  chainSegments={
                    chainOverlayByDiagramKey.get(diagram.key)?.segments ?? []
                  }
                  chainNodeMarkers={
                    chainOverlayByDiagramKey.get(diagram.key)?.nodeMarkers ?? []
                  }
                  hoveredChainName={hoveredChainName}
                  onHoveredChainChange={handleHoveredChainChange}
                  onChainClick={handleChainClick}
                />
              ),
            )}
          </div>
        </BoneAssignmentCategorySection>
        {assignmentMode === 'roles' && additionalRoleSections.length > 0 && (
          <div className="flex flex-col">
            {additionalRoleSections.map((section) => (
              <AdditionalRoleDiagramSection
                key={section.key}
                section={section}
                assignedRoleNames={assignedRoleNames}
                roleAssignments={roleAssignments}
                focusedRoleName={focusedRoleName}
                pendingRoleName={pendingRoleAssignment?.roleName ?? null}
                selectedBoneRef={selectedBoneRef}
                roleAssignmentEnabled={roleAssignmentEnabled}
                formatRoleLabel={formatAssignmentLabel}
                onRoleClick={handleRoleClick}
                onHoveredRoleChange={handleHoveredRoleChange}
                onRoleClear={handleRoleClear}
                onAddChain={handleAdditionalDiagramAdd}
                onChainClear={onClearChain ? handleAdditionalChainClear : undefined}
              />
            ))}
          </div>
        )}
        {assignmentMode === 'chains' && additionalChainSections.length > 0 && (
          <div className="flex flex-col">
            {additionalChainSections.map((section) => (
              <AdditionalChainDiagramSection
                key={`${section.key}-${section.defaultOpen ? 'open' : 'closed'}`}
                section={section}
                activeChainName={activeChainName}
                focusedChainName={focusedChainName}
                hoveredChainName={hoveredChainName}
                pendingChainName={pendingChainAssignment?.chainName ?? null}
                chainAssignmentEnabled={chainAssignmentEnabled}
                onHoveredChainChange={handleHoveredChainChange}
                onChainClick={handleChainClick}
                onChainClear={onClearChain ? handleAdditionalChainClear : undefined}
                onAddChain={handleAdditionalDiagramAdd}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatChainAssignmentTitle(
  assignment: BoneAssignmentChainState | null,
): string {
  if (!assignment?.startBoneName) {
    return 'Unassigned';
  }
  if (
    !assignment.endBoneName ||
    assignment.startBoneName === assignment.endBoneName
  ) {
    return assignment.startBoneName;
  }
  return `${assignment.startBoneName} -> ${assignment.endBoneName}`;
}

function getChainOverlayPriority(
  overlay:
    | BoneAssignmentDiagramChainOverlaySegment
    | BoneAssignmentDiagramChainNodeMarker,
  hoveredChainName: string | null,
): number {
  if (overlay.isPending) return 4;
  if (overlay.chainName === hoveredChainName) return 3;
  if (overlay.isFocused) return 2;
  if (overlay.isAssigned) return 1;
  return 0;
}
